import { useEffect, useState, type ReactNode } from "react";
import { ModalShell } from "../modals/ModalShell";
import { formatYmdLong } from "../../lib/dates";
import { useConfirmClick } from "./hooks/useConfirmClick";
import type { EventRef, RoleOutcome } from "../../lib/journeyApi";
import "../Onboarding/onboarding.css";
import "./planner.css";

/**
 * The ONE role-decision surface (spec §11.8, C1). Replaces `IntentChoiceModal`
 * + `OverlapResolutionSheet` + the wizard `MilestoneConfirmSheet`, driven by the
 * shared geometry outcomes (`@shared/modules/journeys/collisionGeometry`, X0) so
 * client and server never diverge on which options are valid.
 *
 * Rendered per `kind` (the trigger); every trigger routes through here:
 *   - `pair`           — add-event / apply-plan collision, promote-collision,
 *                        anon-merge reconcile (spec §9.1/§9.4/§9.5, T1–T3).
 *                        There is deliberately NO `reclassify` kind — a promote
 *                        that collides reuses `pair`, built from S2's
 *                        `needsCollisionResolution` 409 `conflicts` payload.
 *   - `window_covers`  — a window extend newly covers events (spec §9.2, T4).
 *   - `window_orphans` — a window shrink pushes a tune-up out (spec §9.2, T10).
 *   - `goal_removal`   — deleting/archiving a goal with nested tune-ups (§9.3, T5).
 *
 * This component is PRESENTATION ONLY — it returns a typed `RoleResolutionChoice`
 * via `onResolve` that the caller (C2's `applyRoleResolution`, §2.2) maps to the
 * right API retry. It does not itself call any API.
 */

/** A tune-up row for the `window_orphans` kind — carries the purchased-kit warn
 *  flag (SF16) alongside the base event ref. */
export type OrphanTuneup = EventRef & { hasPurchasedKit?: boolean };

/** G5 consequence preview (server-computed, mirrors the 409's `consequences`
 *  block — see `JourneyOverlapConflict`). Absent (old server / client
 *  pre-check contexts) → no consequence lines render, pixel-identical to the
 *  pre-G5 sheet. */
export type OverlapConsequences = {
  demote?: { removedSessions: number };
  split?: { removedSessions: number };
  chain?: { newStartYmd: string; removedSessions?: number };
} | null;

export type RoleResolutionContext =
  | {
      kind: "pair";
      events: [EventRef, EventRef];
      anchor: EventRef;
      outcomes: RoleOutcome[];
      /**
       * Multi-conflict counter (§6.3): when a create collides with SEVERAL
       * journeys the server 409s once per remaining conflict, and the funnel
       * opens this sheet once per round. `conflictIndex` is 1-based (how many
       * rounds so far, this one included); `conflictCount` the known total
       * (already-resolved + the server's remaining count). When
       * `conflictCount > 1` the eyebrow reads "… · {index} of {count}";
       * absent/1 renders exactly as the single-conflict sheet always has.
       */
      conflictIndex?: number;
      conflictCount?: number;
      /**
       * G5: what each outcome would do to THIS conflict, threaded from the
       * server 409 (`pairContextFromConflict`). Absent → cards render exactly
       * as before (back-compat with an old server and the client pre-check,
       * which has no counts to offer).
       */
      consequences?: OverlapConsequences;
    }
  | { kind: "window_covers"; journeyId: string; events: EventRef[] }
  | { kind: "window_orphans"; journeyId: string; tuneups: OrphanTuneup[] }
  | { kind: "goal_removal"; goal: EventRef; tuneups: EventRef[] };

/**
 * What the user picked. The caller maps this to a server retry per §2.2:
 *   - pair `nest_new`      → retry create `resolveAsMilestone:true` (or anon draft)
 *   - pair `nest_existing` → `overlapResolution:{mode:"demote"}` or `reclassifyEvent`
 *   - pair `chain`      → `overlapResolution:{mode:"chain"}`
 *   - window_covers        → per-event `reclassify`/`pairResolution`/`clampStartYmd`
 *   - window_orphans       → per-tuneup extend/promote-to-goal/remove
 *   - goal_removal         → `tuneupDisposition`
 */
export type RoleResolutionChoice =
  /**
   * `remove_existing` and `remove_existing_event` (robustness spec §3 intent 1,
   * Lane B3) are CLIENT-ONLY: neither is ever sent as an overlap mode.
   *   - `remove_existing`       — archive the existing race's journey via the
   *     Journey-settings primitive (the event row survives as a saved event),
   *     then retry the create.
   *   - `remove_existing_event` — delete the existing race's event AND archive
   *     its journey (paid history kept) via the sanctioned DELETE route, then
   *     retry the create. Only offered when the host passes
   *     `offerRemoveEntirely` (it needs the conflict's `customerEventId` and a
   *     customer principal — the DELETE route is customer-only).
   * Both are only offered when the sheet host passes `offerRemoveExisting`.
   */
  | { kind: "pair"; outcome: RoleOutcome | "remove_existing" | "remove_existing_event" }
  | { kind: "window_covers"; eventId: string; action: "reclassify" | "clamp_start" | "nest_existing" | "split" }
  | { kind: "window_orphans"; eventId: string; action: "extend" | "keep_as_goal" | "remove" }
  | { kind: "goal_removal"; disposition: "keep_as_goal" | "remove" };

function dateSuffix(ymd: string): string {
  const label = ymd ? formatYmdLong(ymd) : "";
  return label ? ` (${label})` : "";
}

/**
 * A tappable option card (reuses the onboarding `.fgo-optcard` pattern).
 *
 * Two modes:
 *  - default (click-to-commit) — used by the per-row kinds (window_covers,
 *    window_orphans, goal_removal) where each row is its own independent action.
 *  - `selectable` — used by the single-decision `pair` kind: the card shows a
 *    radio dot and only SELECTS on click (`is-on`); a footer "Apply" button
 *    commits. This makes the cards read as choices, not one-tap notifications.
 */
/** A short status chip shown next to an option's title (mockup nuance): the
 *  impact at a glance — amber = removes/trims workouts, grey = neutral
 *  structural note, green = nothing removed. */
export type OptionBadge = { text: string; tone: "amber" | "grey" | "green" };

export function OptionCard({
  label,
  badge,
  hint,
  consequence,
  onClick,
  advanced,
  selectable,
  selected,
  dataAttr
}: {
  label: string;
  /** Optional status chip beside the title (pair sheet). Absent → nothing. */
  badge?: OptionBadge;
  hint: ReactNode;
  /** Optional muted second hint line — e.g. Lane B2's server-computed impact
   *  ("removes 12 upcoming sessions"). Absent → renders nothing. */
  consequence?: ReactNode;
  onClick: () => void;
  advanced?: boolean;
  selectable?: boolean;
  selected?: boolean;
  dataAttr?: Record<string, string>;
}) {
  return (
    <button
      type="button"
      className={
        `fgo-optcard${advanced ? " fgo-optcard--advanced" : ""}` +
        (selectable ? " fgo-optcard--selectable" : "") +
        (selected ? " is-on" : "")
      }
      aria-pressed={selectable ? !!selected : undefined}
      onClick={onClick}
      {...dataAttr}>
      {selectable && <span className="fgo-optcard__radio" aria-hidden />}
      <span className="fgo-optcard__content">
        <span className="fgo-optcard__head">
          <span className="fgo-optcard__label">{label}</span>
          {badge && (
            <span className={`fgp-rr-badge fgp-rr-badge--${badge.tone}`}>{badge.text}</span>
          )}
        </span>
        <span className="fgo-optcard__hint">{hint}</span>
        {consequence && <span className="fgo-optcard__consequence">{consequence}</span>}
      </span>
    </button>
  );
}

/** One selectable `pair` option (built from the geometry outcomes + the pair
 *  direction). The finalized model offers EXACTLY TWO cards per pair, worded by
 *  direction relative to the existing race. */
export type PairCard = {
  outcome: RoleOutcome;
  label: string;
  /** Status chip beside the title (impact at a glance). */
  badge?: OptionBadge;
  hint: ReactNode;
  /** Muted impact line under the hint — populated from the 409's
   *  `consequences`; absent → nothing renders. May carry `<strong>` on the
   *  existing race for the destructive options. */
  consequence?: ReactNode;
  /**
   * Destructive = this option removes upcoming workouts from the OTHER
   * (existing) race's plan → Apply becomes a two-step confirm. Only the
   * This-earlier `chain` and This-later `nest_existing` picks are destructive,
   * and only when the server reports removedSessions > 0.
   */
  destructive?: boolean;
  /** How many workouts this destructive pick removes (drives the armed confirm
   *  button copy). Only set when `destructive`. */
  removedCount?: number;
  /** The existing (other) race — named in the armed confirm button for
   *  destructive picks. */
  existingName?: string;
};

/**
 * The two `pair` cards — direction-aware (finalized owner-locked model). "This"
 * = the race being added (`events[0]`, the pair context's new event); "the
 * existing race" = `events[1]`. Compare their ymd to pick the wording:
 *
 *  - THIS is EARLIER → `["nest_new", "chain"]`
 *      • nest_new  = "Add {This} as a prep race"      (non-destructive)
 *      • chain     = "Keep them separate" — {This} gets its own plan; the
 *                    EXISTING (later) plan trims upcoming workouts and resumes
 *                    after {This}. (DESTRUCTIVE — trims the existing plan)
 *  - THIS is LATER  → `["nest_existing", "chain"]`
 *      • nest_existing = "Make {This} your goal" — the EXISTING (earlier) race
 *                        becomes a prep race, its plan is archived.
 *                        (DESTRUCTIVE — removes the existing plan's workouts)
 *      • chain         = "Keep them separate" — each keeps its own plan; {This}'s
 *                        plan starts after the existing race. (non-destructive)
 *
 * The OTHER race (the one being trimmed/demoted) is BOLD in the two destructive
 * options — that's how the user learns it's their other race losing workouts.
 * ⚠️ No card may promise a race-day kit (milestone kit generation is P5).
 * Exported for tests.
 */
export function buildPairCards(
  events: [EventRef, EventRef],
  _anchor: EventRef,
  outcomes: RoleOutcome[],
  consequences?: OverlapConsequences
): PairCard[] {
  const [thisEvent, existing] = events;
  const thisName = thisEvent.name || "this race";
  const existingName = existing.name || "the existing race";
  const thisEarlier = thisEvent.ymd <= existing.ymd;
  const has = (o: RoleOutcome) => outcomes.includes(o);
  const cards: PairCard[] = [];

  const resumeDate = consequences?.chain?.newStartYmd
    ? formatYmdLong(consequences.chain.newStartYmd)
    : null;

  const plural = (n: number) => (n === 1 ? "workout" : "workouts");

  // Impact chip beside each title (mockup nuance). Amber = removes/trims
  // workouts; grey = neutral structural note; green = nothing removed. The
  // per-card "paid boxes stay in your history" copy is lifted to ONE reassurance
  // line below the cards, so the chips + hints stay to a single idea each.
  const removesBadge = (n: number, verb: "Removes" | "Trims"): OptionBadge => ({
    text: `${verb} ${n} ${plural(n)}`,
    tone: "amber"
  });

  if (thisEarlier) {
    // nest_new — non-destructive: This rides the existing plan.
    if (has("nest_new")) {
      cards.push({
        outcome: "nest_new",
        label: `Add ${thisName} as a prep race`,
        badge: { text: "No new workouts", tone: "green" },
        hint: `It rides your ${existingName} plan as a tune-up.`
      });
    }
    // chain — DESTRUCTIVE (trims the existing later plan) when it removes work.
    if (has("chain")) {
      const removed = consequences?.chain?.removedSessions;
      const destructive = typeof removed === "number" && removed > 0;
      cards.push({
        outcome: "chain",
        label: "Keep them separate",
        badge: destructive
          ? removesBadge(removed!, "Trims")
          : { text: "Two plans, back to back", tone: "grey" },
        hint: (
          <>
            {thisName} gets its own plan; <strong>{existingName}</strong> resumes after it
            {resumeDate ? ` on ${resumeDate}` : ""}.
          </>
        ),
        destructive,
        removedCount: destructive ? removed : undefined,
        existingName
      });
    }
  } else {
    // nest_existing — DESTRUCTIVE (removes the existing earlier plan) when it
    // removes work.
    if (has("nest_existing")) {
      const removed = consequences?.demote?.removedSessions;
      const destructive = typeof removed === "number" && removed > 0;
      cards.push({
        outcome: "nest_existing",
        label: `Make ${thisName} your goal`,
        badge: destructive
          ? removesBadge(removed!, "Removes")
          : { text: "One combined plan", tone: "grey" },
        hint: (
          <>
            <strong>{existingName}</strong> becomes a prep race — its plan is archived.
          </>
        ),
        destructive,
        removedCount: destructive ? removed : undefined,
        existingName
      });
    }
    // chain — non-destructive: nothing deleted, This starts after the existing.
    if (has("chain")) {
      cards.push({
        outcome: "chain",
        label: "Keep them separate",
        badge: { text: "Two plans, back to back", tone: "grey" },
        hint: (
          <>
            Each keeps its own plan; your {thisName} plan starts after {existingName}
            {resumeDate ? ` on ${resumeDate}` : ""}.
          </>
        )
      });
    }
  }
  return cards;
}

/** `pair` body — two selectable cards (radio) + a short intro line. The commit
 *  lives in the sheet footer ("Apply", disabled until a card is picked; a
 *  destructive pick arms a two-step confirm — see the sheet footer). Two
 *  options are ALWAYS present for a pair, so there is no single-card mode. */
function PairBody({
  events,
  cards,
  selected,
  onSelect,
  extraActions,
  removeExisting
}: {
  events: [EventRef, EventRef];
  cards: PairCard[];
  selected: RoleOutcome | null;
  onSelect: (outcome: RoleOutcome) => void;
  /** Escape-hatch slot (Lanes B2/B3/F1) — rendered at the bottom of the body
   *  as a visually-secondary region; nothing renders when unset. */
  extraActions?: ReactNode;
  /** Sheet-NATIVE "Remove {existing race} instead" block (B3) — built by the
   *  sheet itself (it needs the confirm-click hook), rendered in the same
   *  secondary region above any caller-supplied `extraActions`. */
  removeExisting?: ReactNode;
}) {
  const [thisEvent, existing] = events;
  const thisName = thisEvent.name || "this race";
  const existingName = existing.name || "the existing race";
  const thisEarlier = thisEvent.ymd <= existing.ymd;

  // Intro: name both races + why they conflict, then the ask. "falls inside"
  // when the added race sits within the existing plan, "overlaps" otherwise.
  const intro = (
    <p className="fgp-window-caption fgp-rr-pairintro" data-testid="rr-pair-intro">
      <strong>{thisName}</strong>
      {dateSuffix(thisEvent.ymd)} {thisEarlier ? "falls inside" : "overlaps"} your{" "}
      <strong>{existingName}</strong> training plan{dateSuffix(existing.ymd)}. Only one can
      anchor a plan here — pick how they fit.
    </p>
  );

  return (
    <div className="fgp-modalshell__pad">
      {intro}
      <div className="fgp-funnel-rows" role="radiogroup" aria-label="How the races fit together">
        {cards.map((c) => (
          <OptionCard
            key={c.outcome}
            selectable
            selected={selected === c.outcome}
            dataAttr={{ "data-outcome": c.outcome }}
            label={c.label}
            badge={c.badge}
            hint={c.hint}
            consequence={c.consequence}
            onClick={() => onSelect(c.outcome)}
          />
        ))}
      </div>
      {/* One reassurance line instead of repeating it on every destructive card. */}
      <p className="fgp-rr-reassure">
        <span className="fgp-rr-reassure__check" aria-hidden>✓</span>
        Paid fuel boxes always stay in your history — whatever you choose.
      </p>
      {removeExisting && <div className="fgp-rr-removeblock">{removeExisting}</div>}
      {extraActions && (
        <div className="fgp-rr-extra" data-testid="rr-extra-actions">
          <div className="fgp-rr-extra__row">{extraActions}</div>
        </div>
      )}
    </div>
  );
}

/**
 * One sheet-native destructive secondary action (robustness spec §3 intent 1,
 * Lane B3). Destructive, so it's a two-step inline confirm (`useConfirmClick` —
 * first click ARMS, the label flips to "Really…?", a second click within 3s
 * commits; the arm auto-resets otherwise). Each instance owns its OWN confirm
 * hook, so arming one action never arms its sibling.
 */
function RemoveAction({
  label,
  confirmLabel,
  support,
  testidBase,
  onConfirm
}: {
  label: string;
  confirmLabel: string;
  support: ReactNode;
  /** `data-testid` is `{testidBase}` idle and `{testidBase}-confirm` armed. */
  testidBase: string;
  onConfirm: () => void;
}) {
  const { confirming, trigger } = useConfirmClick(onConfirm);
  return (
    <div className={`fgp-rr-removecard${confirming ? " is-armed" : ""}`}>
      <button
        type="button"
        className="fgp-rr-removecard__title"
        data-testid={confirming ? `${testidBase}-confirm` : testidBase}
        onClick={trigger}>
        {confirming ? confirmLabel : label}
      </button>
      <span className="fgp-rr-removecard__support">{support}</span>
    </div>
  );
}

/** The mockup's collapsible "Remove a race instead" disclosure — the two
 *  destructive remove actions live behind a red toggle so they don't compete
 *  with the primary resolution cards. Collapsed by default. */
function RemoveRaceDisclosure({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`fgp-rr-removedisc${open ? " is-open" : ""}`}>
      <button
        type="button"
        className="fgp-rr-removedisc__toggle"
        aria-expanded={open}
        data-testid="rr-remove-disclosure"
        onClick={() => setOpen((o) => !o)}>
        Remove a race instead
        <span className="fgp-rr-removedisc__chev" aria-hidden>
          ⌄
        </span>
      </button>
      {open && <div className="fgp-rr-removedisc__body">{children}</div>}
    </div>
  );
}

/**
 * The remove-race CHOICE (robustness spec §3 intent 1): up to TWO destructive
 * actions, each with its own independent two-step confirm.
 *   1. "Remove {race}'s training plan" — the client-only `remove_existing`
 *      outcome: the host archives the existing race's journey (the event row
 *      SURVIVES as a saved event) and retries its create.
 *   2. "Remove {race} entirely" — the client-only `remove_existing_event`
 *      outcome: the host deletes the event AND archives its journey (paid
 *      history kept), then retries. Rendered ONLY when `offerEntirely` (the
 *      conflict carried a `customerEventId` and the principal can call the
 *      customer-only DELETE route).
 */
function RemoveExistingActions({
  existing,
  consequences,
  offerEntirely,
  onRemovePlan,
  onRemoveEvent
}: {
  existing: EventRef;
  consequences?: OverlapConsequences;
  offerEntirely: boolean;
  onRemovePlan: () => void;
  onRemoveEvent: () => void;
}) {
  const removed = consequences?.demote?.removedSessions;
  return (
    <>
      <RemoveAction
        label={`Remove ${existing.name}'s training plan`}
        confirmLabel={`Really remove ${existing.name}'s training plan?`}
        testidBase="rr-remove-existing"
        onConfirm={onRemovePlan}
        support={
          <>
            The race stays in your calendar as a saved event.
            {typeof removed === "number" && removed > 0 && (
              <>
                {" "}
                Removes {removed} upcoming session{removed === 1 ? "" : "s"}; paid boxes stay
                in your history.
              </>
            )}
          </>
        }
      />
      {offerEntirely && (
        <RemoveAction
          label={`Remove ${existing.name} entirely`}
          confirmLabel={`Really remove ${existing.name} entirely?`}
          testidBase="rr-remove-entirely"
          onConfirm={onRemoveEvent}
          support={
            <>
              Removes the race and its training plan from your calendar — paid boxes stay in
              your history.
            </>
          }
        />
      )}
    </>
  );
}

type WindowCoversAction = "reclassify" | "clamp_start" | "nest_existing" | "split";

/** The valid actions for one covered event — a goal-with-journey offers the
 *  pair outcomes (Gap 3); a bare event offers reclassify + stop-before. */
function coversRowOptions(ev: EventRef): {
  action: WindowCoversAction;
  label: string;
  hint: string;
  advanced?: boolean;
}[] {
  return ev.hasJourney
    ? [
        {
          action: "nest_existing",
          label: `Make ${ev.name} a prep race`,
          hint: "It nests into this training plan; its own race plan archives."
        },
        {
          action: "split",
          advanced: true,
          label: `End ${ev.name}'s training plan early`,
          hint: "Shorten its window so this training plan takes over its final weeks."
        },
        {
          action: "clamp_start",
          label: "Stop the window before it",
          hint: "Keep both as full training plans — don't extend this window past it."
        }
      ]
    : [
        {
          action: "reclassify",
          label: "Add as a prep race",
          hint: `${ev.name} rides this training plan as a prep race.`
        },
        {
          action: "clamp_start",
          label: "Stop the window before it",
          hint: "Clamp the start so this training plan ends before that race."
        }
      ];
}

/** `window_covers` (T4) — per-event rows, each a radiogroup. Every row must be
 *  answered, then ONE Apply (in the footer) commits them all as a batch. */
function WindowCoversBody({
  events,
  selected,
  onSelect
}: {
  events: EventRef[];
  selected: Map<string, WindowCoversAction>;
  onSelect: (eventId: string, action: WindowCoversAction) => void;
}) {
  return (
    <div className="fgp-modalshell__pad">
      <p className="fgp-window-caption fgp-rr-pairintro">
        Extending this training plan pulls in {events.length === 1 ? "another race" : "more races"}.
        Choose what happens to {events.length === 1 ? "it" : "each"}, then Apply.
      </p>
      {events.map((ev) => (
        <div key={ev.id} className="fgp-rr-eventgroup" data-event-id={ev.id}>
          <div className="fgp-window-caption">
            Including these weeks pulls in <strong>{ev.name}</strong>
            {dateSuffix(ev.ymd)}.
          </div>
          <div
            className="fgp-funnel-rows"
            role="radiogroup"
            aria-label={`What happens to ${ev.name}`}>
            {coversRowOptions(ev).map((o) => (
              <OptionCard
                key={o.action}
                selectable
                selected={selected.get(ev.id) === o.action}
                advanced={o.advanced}
                dataAttr={{ "data-action": o.action }}
                label={o.label}
                hint={o.hint}
                onClick={() => onSelect(ev.id, o.action)}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** `window_orphans` (T10) — per-tune-up re-home rows: Extend to keep / Make its
 *  own goal / Remove. SF16: warn (not block) when a purchased kit is at stake. */
function WindowOrphansBody({
  tuneups,
  onCommit
}: {
  tuneups: OrphanTuneup[];
  /** Click-to-commit: each row's `extend` mutates the shared window (it isn't
   *  independent per-row), so this kind stays one-tap rather than batched. */
  onCommit: (choice: RoleResolutionChoice) => void;
}) {
  return (
    <div className="fgp-modalshell__pad">
      {tuneups.map((tu) => (
        <div key={tu.id} className="fgp-rr-eventgroup" data-event-id={tu.id}>
          <div className="fgp-window-caption">
            This window no longer covers <strong>{tu.name}</strong>
            {dateSuffix(tu.ymd)}.
            {tu.hasPurchasedKit && (
              <span className="fgp-rr-warn"> Its purchased kit stays in your history.</span>
            )}
          </div>
          <div className="fgp-funnel-rows">
            <OptionCard
              dataAttr={{ "data-action": "extend" }}
              label="Extend to keep it"
              hint="Widen the window back so this prep race stays inside the training plan."
              onClick={() =>
                onCommit({ kind: "window_orphans", eventId: tu.id, action: "extend" })
              }
            />
            <OptionCard
              dataAttr={{ "data-action": "keep_as_goal" }}
              label="Make it its own goal"
              hint="It becomes a standalone goal race (no training plan until you make one)."
              onClick={() =>
                onCommit({ kind: "window_orphans", eventId: tu.id, action: "keep_as_goal" })
              }
            />
            <OptionCard
              dataAttr={{ "data-action": "remove" }}
              label="Remove it"
              hint="Drop the prep race from your calendar."
              onClick={() =>
                onCommit({ kind: "window_orphans", eventId: tu.id, action: "remove" })
              }
            />
          </div>
        </div>
      ))}
    </div>
  );
}

/** `goal_removal` (T5) — one disposition for all nested prep races. A single
 *  decision, so select-then-Apply (footer) like `pair`. */
function GoalRemovalBody({
  goal,
  tuneups,
  selected,
  onSelect
}: {
  goal: EventRef;
  tuneups: EventRef[];
  selected: "keep_as_goal" | "remove" | null;
  onSelect: (disposition: "keep_as_goal" | "remove") => void;
}) {
  const list = tuneups.map((t) => t.name).join(", ");
  return (
    <div className="fgp-modalshell__pad">
      <div className="fgp-window-caption fgp-rr-pairintro">
        Your {goal.name} training plan contains {tuneups.length} prep race
        {tuneups.length === 1 ? "" : "s"}
        {list ? ` (${list})` : ""}. Choose what happens to {tuneups.length === 1 ? "it" : "them"},
        then Apply.
      </div>
      <div className="fgp-funnel-rows" role="radiogroup" aria-label="What happens to the prep races">
        <OptionCard
          selectable
          selected={selected === "keep_as_goal"}
          dataAttr={{ "data-action": "keep_as_goal" }}
          label="Keep them as races"
          hint="Each prep race becomes a standalone goal race — their saved kits stay in history."
          onClick={() => onSelect("keep_as_goal")}
        />
        <OptionCard
          selectable
          selected={selected === "remove"}
          dataAttr={{ "data-action": "remove" }}
          label="Remove them too"
          hint="Delete the prep races along with this training plan."
          onClick={() => onSelect("remove")}
        />
      </div>
    </div>
  );
}

const KIND_HEAD: Record<
  RoleResolutionContext["kind"],
  { eyebrow: string; title: string }
> = {
  pair: { eyebrow: "Schedule conflict", title: "Two races, one training window" },
  window_covers: { eyebrow: "Extending this training plan", title: "This window pulls in more races" },
  window_orphans: { eyebrow: "Shrinking this training plan", title: "A prep race falls outside this window" },
  goal_removal: { eyebrow: "Removing this training plan", title: "This training plan has prep races" }
};

export function RoleResolutionSheet({
  context,
  onResolve,
  onCancel,
  extraActions,
  offerRemoveExisting,
  offerRemoveEntirely
}: {
  context: RoleResolutionContext;
  /**
   * Escape-hatch slot (`pair` kind only, Lanes B2/B3/F1 — "Just save the
   * event", "Edit the date instead"). Rendered at the bottom of the pair body
   * in a visually-secondary region; renders nothing when unset.
   */
  extraActions?: ReactNode;
  /**
   * B3 (spec §3 intent 1): render the sheet-NATIVE "Remove {existing race}
   * instead" secondary action on a `pair` sheet. Opt-in because only hosts
   * that can act on the resolution (the funnel's 409 loop, which knows the
   * conflicting journey id) may offer it — the client pre-check sheet must
   * not. Requires exactly one of the pair to already anchor a build
   * (`hasJourney`); ambiguous pairs render nothing.
   */
  offerRemoveExisting?: boolean;
  /**
   * Remove-race CHOICE (robustness spec Part 2): additionally render the
   * "Remove {race} entirely" action (client-only `remove_existing_event`
   * outcome — delete the event AND archive its journey). Only meaningful
   * alongside `offerRemoveExisting`; hosts pass it ONLY when the conflict's
   * `existing.customerEventId` is present AND the principal is a customer
   * (the DELETE route is customer-only). Absent → only the archive action
   * renders (its copy is already honest for that case).
   */
  offerRemoveEntirely?: boolean;
  /**
   * Commit one or more resolutions. Single-decision kinds (`pair`,
   * `goal_removal`) emit a 1-element array on Apply; `window_covers` emits ALL
   * its row choices at once on Apply; `window_orphans` emits one per click (its
   * rows aren't independent — see WindowOrphansBody). The caller folds the batch
   * into one PATCH / retry.
   */
  onResolve: (choices: RoleResolutionChoice[]) => void;
  onCancel: () => void;
}) {
  const head = KIND_HEAD[context.kind];
  // §6.3 counter: only the pair kind carries it, and only a real multi-conflict
  // sequence (> 1) changes the eyebrow — a lone conflict stays pixel-identical.
  const eyebrow =
    context.kind === "pair" && (context.conflictCount ?? 1) > 1
      ? `${head.eyebrow} · ${context.conflictIndex ?? 1} of ${context.conflictCount}`
      : head.eyebrow;

  // pair: two direction-aware cards; goal_removal + window_covers are
  // select-then-Apply. window_orphans stays click-to-commit (its rows share a
  // window, so they can't be independently batched).
  const pairCards =
    context.kind === "pair"
      ? buildPairCards(context.events, context.anchor, context.outcomes, context.consequences)
      : [];
  // B3: the removable EXISTING race — exactly one of the pair anchoring a
  // build (the funnel's new event never does). Two-with-journeys (I2
  // reconcile) or none is ambiguous → no remove action.
  const pairWithJourney =
    context.kind === "pair" ? context.events.filter((e) => e.hasJourney) : [];
  const removableExisting =
    offerRemoveExisting && context.kind === "pair" && pairWithJourney.length === 1
      ? pairWithJourney[0]
      : null;
  const [pairOutcome, setPairOutcome] = useState<RoleOutcome | null>(null);
  const [goalDisposition, setGoalDisposition] = useState<"keep_as_goal" | "remove" | null>(null);
  const [coversSel, setCoversSel] = useState<Map<string, WindowCoversAction>>(new Map());
  const selectCovers = (eventId: string, action: WindowCoversAction) =>
    setCoversSel((prev) => new Map(prev).set(eventId, action));

  // Two-step Apply arm (requirement #2): when the selected pair card is
  // destructive (it removes upcoming workouts from the OTHER race's plan), the
  // first Apply click ARMS a confirm; the second commits. Selecting a different
  // card RESETS the arm.
  const selectedCard =
    context.kind === "pair" ? pairCards.find((c) => c.outcome === pairOutcome) ?? null : null;
  const [pairArmed, setPairArmed] = useState(false);
  useEffect(() => {
    setPairArmed(false);
  }, [pairOutcome]);

  // Apply-enablement + the batch each Apply commits, per kind.
  const coversReady =
    context.kind === "window_covers" && context.events.every((e) => coversSel.has(e.id));

  const applyDisabled =
    (context.kind === "pair" && pairOutcome === null) ||
    (context.kind === "goal_removal" && goalDisposition === null) ||
    (context.kind === "window_covers" && !coversReady);

  const commitPair = (outcome: RoleOutcome) => onResolve([{ kind: "pair", outcome }]);

  const onApplyClick = () => {
    if (context.kind === "pair" && pairOutcome) {
      // Destructive pick → first click arms, second commits.
      if (selectedCard?.destructive && !pairArmed) {
        setPairArmed(true);
        return;
      }
      commitPair(pairOutcome);
      return;
    }
    if (context.kind === "goal_removal" && goalDisposition) {
      onResolve([{ kind: "goal_removal", disposition: goalDisposition }]);
    } else if (context.kind === "window_covers" && coversReady) {
      onResolve(
        [...coversSel.entries()].map(([eventId, action]) => ({
          kind: "window_covers" as const,
          eventId,
          action
        }))
      );
    }
  };

  // window_orphans is the only click-to-commit kind → Cancel-only footer.
  const showApply = context.kind !== "window_orphans";

  // Armed destructive confirm label — names the affected existing race in BOLD
  // and the workout count, so the destructive intent is unmistakable.
  const applyArmed = context.kind === "pair" && !!selectedCard?.destructive && pairArmed;
  const applyLabel: ReactNode = applyArmed ? (
    <>
      Remove {selectedCard!.removedCount}{" "}
      {selectedCard!.removedCount === 1 ? "workout" : "workouts"} from{" "}
      <strong>{selectedCard!.existingName}</strong>?
    </>
  ) : (
    <>
      Apply <span aria-hidden>→</span>
    </>
  );

  const footer = (
    <>
      {showApply ? (
        <button
          type="button"
          className="fg-btn fg-btn--ghost fg-btn--sm"
          onClick={onCancel}>
          Cancel
        </button>
      ) : (
        <span />
      )}
      {showApply ? (
        <button
          type="button"
          className="fg-btn fg-btn--primary fg-btn--sm"
          style={
            applyArmed ? { background: "var(--fg-danger-ink, #C42727)" } : undefined
          }
          disabled={applyDisabled}
          data-testid={applyArmed ? "rr-apply-confirm" : "rr-apply"}
          onClick={onApplyClick}>
          {applyLabel}
        </button>
      ) : (
        <button
          type="button"
          className="fg-btn fg-btn--ghost fg-btn--sm"
          onClick={onCancel}>
          Cancel
        </button>
      )}
    </>
  );

  return (
    <ModalShell
      eyebrow={eyebrow}
      title={head.title}
      onClose={onCancel}
      width={560}
      footer={footer}>
      {context.kind === "pair" && (
        <PairBody
          events={context.events}
          cards={pairCards}
          selected={pairOutcome}
          onSelect={setPairOutcome}
          extraActions={extraActions}
          removeExisting={
            removableExisting ? (
              <RemoveRaceDisclosure>
                <RemoveExistingActions
                  existing={removableExisting}
                  consequences={context.consequences}
                  offerEntirely={!!offerRemoveEntirely}
                  onRemovePlan={() =>
                    onResolve([{ kind: "pair", outcome: "remove_existing" }])
                  }
                  onRemoveEvent={() =>
                    onResolve([{ kind: "pair", outcome: "remove_existing_event" }])
                  }
                />
              </RemoveRaceDisclosure>
            ) : undefined
          }
        />
      )}
      {context.kind === "window_covers" && (
        <WindowCoversBody events={context.events} selected={coversSel} onSelect={selectCovers} />
      )}
      {context.kind === "window_orphans" && (
        <WindowOrphansBody
          tuneups={context.tuneups}
          onCommit={(choice) => onResolve([choice])}
        />
      )}
      {context.kind === "goal_removal" && (
        <GoalRemovalBody
          goal={context.goal}
          tuneups={context.tuneups}
          selected={goalDisposition}
          onSelect={setGoalDisposition}
        />
      )}
    </ModalShell>
  );
}
